// V2 directional attack projectile — the shipped shader since 2026-07-05
// (approved on /vfx-lab). Same uniform contract as attackProjectile.frag.ts
// (uTime/uDuration/uFrom/uTo/uAspect/uTint/uType); V1 is kept for reference.
//
// Per type:
//   0 physical  — "flash cut": charge at the dealer, an instant blinding
//                 line across the path that dissolves dealer→target, target
//                 erupts at ~28% on its own timeline (skips shared impact)
//   1 fire      — teardrop flame head (stretched behind), fbm flame tongue
//                 in the trail, drifting embers; impact fireball with a
//                 noisy edge + ember starburst
//   2 ice       — 6-fold crystal head, frost-mist trail, glinting sparkles;
//                 impact grows 8 radial ice spikes + a frost ring
//   3 lightning — main bolt + 2 forked branch bolts, source pre-glow;
//                 impact crackle arcs flickering around the target
//   4 arcane    — orb with inner swirl + 3 orbiting satellites, wavy ribbon
//                 trail; impact rotating dashed rune ring + spiral flash
//   5 earth     — 3 tumbling chunks, wide dust trail, falling pebbles;
//                 impact crack-star + slow lingering dust ring
export const ATTACK_PROJECTILE_V2_FRAG = /* glsl */ `
  precision highp float;
  varying vec2 vUv;
  uniform float uTime;
  uniform float uDuration;
  uniform vec2  uFrom;     // dealer anchor (uv)
  uniform vec2  uTo;       // target anchor (uv)
  uniform vec2  uAspect;   // (aspect,1) or (1,1/aspect) — keep shapes round
  uniform vec3  uTint;
  // float (not int): GLSL ES 1.00 / WebGL1 drivers (Mali/Adreno) can black-screen
  // on int-uniform == chains, so we compare with abs(uType-N) < 0.5 throughout.
  uniform float uType;
  bool isType(float n){ return abs(uType - n) < 0.5; }

  // ---- helpers ----
  float hash(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
  float noise(vec2 p){
    vec2 i = floor(p), f = fract(p);
    f = f*f*(3.0-2.0*f);
    float a = hash(i), b = hash(i+vec2(1,0)), c = hash(i+vec2(0,1)), d = hash(i+vec2(1,1));
    return mix(mix(a,b,f.x), mix(c,d,f.x), f.y);
  }
  float fbm(vec2 p){
    float v = 0.0, a = 0.5;
    for(int i=0;i<4;i++){ v += a*noise(p); p *= 2.02; a *= 0.5; }
    return v;
  }
  float segDist(vec2 p, vec2 a, vec2 b){
    vec2 pa = p-a, ba = b-a;
    float h = clamp(dot(pa,ba)/max(dot(ba,ba),1e-5), 0.0, 1.0);
    return length(pa - ba*h);
  }
  float easeOut(float t){ return 1.0 - pow(1.0-t, 3.0); }
  // round speck field: hash-gated dots (not square cells) on a scrolling grid
  float specks(vec2 grid, float threshold){
    vec2 cell = floor(grid);
    vec2 local = fract(grid) - 0.5;
    return step(threshold, hash(cell)) * smoothstep(0.38, 0.12, length(local));
  }

  void main(){
    float t = clamp(uTime / max(uDuration, 0.0001), 0.0, 1.0);
    vec2 p   = vUv * uAspect;
    vec2 a   = uFrom * uAspect;
    vec2 b   = uTo   * uAspect;
    vec2 dir = b - a;
    float len = max(length(dir), 1e-4);
    vec2 ndir = dir / len;
    vec2 perp = vec2(-ndir.y, ndir.x);

    vec3 col = vec3(0.0);
    float alpha = 0.0;

    float travelT = clamp(t / 0.78, 0.0, 1.0);
    float headPos = easeOut(travelT);
    vec2  head    = mix(a, b, headPos);
    float impactT = clamp((t - 0.74) / 0.26, 0.0, 1.0);
    // Head + trail dissolve as the projectile arrives — the impact burst takes
    // over. Without this the head sits parked on the target through the whole
    // impact window.
    float headFade = 1.0 - smoothstep(0.78, 0.92, t);
    // Force everything to zero on the final frames: uTime keeps ticking after
    // t clamps to 1, so any residual term (crystal head, ice spikes, dust
    // plume) would otherwise freeze on the target until the layer retires the
    // projectile (~200ms later).
    float endFade = 1.0 - smoothstep(0.86, 1.0, t);

    // position of p along/off the flight line (for trails and local frames)
    float along = clamp(dot(p - a, ndir) / len, 0.0, 1.0);
    float offAxis = dot(p - a, perp);

    if (isType(3.0)) {
      // ---- LIGHTNING V2: forked bolt + source pre-glow + impact crackle ----
      float env = exp(-t * 4.5) * (0.6 + 0.4 * step(0.5, fract(uTime * 37.0)));
      float wig = (fbm(vec2(along * 9.0, uTime * 22.0)) - 0.5) * 0.09;
      float d = segDist(p + perp * wig, a, b);
      float core = smoothstep(0.012, 0.0, d) * env;
      float glow = smoothstep(0.06, 0.0, d) * env * 0.5;

      // 2 branch bolts forking off at 35% / 60%, shorter + thinner, each
      // flickering on its own cadence.
      float branches = 0.0;
      for (int k = 0; k < 2; k++) {
        float fk = float(k);
        float root = 0.35 + fk * 0.25;
        float side = fk * 2.0 - 1.0;               // -1, +1
        vec2 ba2 = mix(a, b, root);
        vec2 bb2 = ba2 + ndir * len * 0.22 + perp * side * len * 0.2;
        float wig2 = (fbm(vec2(along * 13.0 + fk * 7.0, uTime * 19.0)) - 0.5) * 0.06;
        float bd = segDist(p + perp * wig2, ba2, bb2);
        float flick = step(0.35, fract(uTime * 29.0 + fk * 0.4));
        branches += smoothstep(0.007, 0.0, bd) * flick;
      }
      branches *= env;

      // source pre-glow: charge at the dealer during the first frames
      float src = smoothstep(0.05, 0.0, length(p - a)) * exp(-t * 9.0);

      col = uTint * (core * 2.2 + glow + branches * 1.6 + src)
        + vec3(core + branches * 0.5) * 0.6;
      alpha = core + glow + branches + src;

      // impact crackle: 3 short jittered arcs dancing around the target
      if (impactT > 0.0) {
        float crackle = 0.0;
        for (int k = 0; k < 3; k++) {
          float fk = float(k);
          float aa = fk * 2.094 + uTime * 13.0;
          vec2 ca = b + vec2(cos(aa), sin(aa)) * 0.035;
          vec2 cb = b + vec2(cos(aa + 1.6), sin(aa + 1.6)) * 0.06;
          crackle += smoothstep(0.006, 0.0, segDist(p, ca, cb));
        }
        crackle *= step(0.5, fract(uTime * 31.0)) * (1.0 - impactT);
        col += mix(uTint, vec3(1.0), 0.5) * crackle * 2.0;
        alpha = max(alpha, crackle);
      }
    } else {
      float dHead = length(p - head);
      float dTrail = segDist(p, a, head);
      float body = 0.0;
      vec3 c = vec3(0.0);

      if (isType(1.0)) {
        // ---- FIRE V2: teardrop flame + fbm tongue + embers ----
        // Stretch the head-distance metric behind the head so it reads as a
        // teardrop pointing along the flight.
        float proj = dot(p - head, ndir);
        float perpD = dot(p - head, perp);
        float stretch = proj > 0.0 ? 1.0 : 0.38; // long tail behind
        float flameD = length(vec2(perpD, proj * stretch));
        float orb  = smoothstep(0.055, 0.0, flameD);
        float halo = smoothstep(0.12, 0.0, flameD) * 0.45;

        // flame tongue: fbm turbulence licking along the travelled segment
        float tongueMask = smoothstep(0.05, 0.0, dTrail) * step(along, headPos);
        float turb = fbm(vec2(along * 10.0 - uTime * 7.0, offAxis * 26.0));
        float tongue = tongueMask * smoothstep(0.35, 0.8, turb);

        // embers: round specks drifting off the trail, biased upward
        float ember = specks(p * 70.0 + vec2(0.0, -uTime * 8.0), 0.965)
          * smoothstep(0.09, 0.0, dTrail) * step(along, headPos);

        body = orb * 1.7 + halo + tongue * 0.9;
        c = mix(uTint, vec3(1.0, 0.95, 0.7), orb) * body;
        c += uTint * tongue * 0.9;
        c += vec3(1.0, 0.62, 0.2) * ember * 1.2;
        body += ember;
        body *= headFade;
        c *= headFade;

        // impact fireball: noisy-edged bloom + ember starburst
        float dT0 = length(p - b);
        if (impactT > 0.0 && dT0 < 0.32) {
          float edge = fbm(vec2(atan(p.y - b.y, p.x - b.x) * 2.5, impactT * 4.0));
          float ballR = impactT * 0.15 * (0.8 + 0.4 * edge);
          float ball = smoothstep(ballR, ballR * 0.4, dT0) * (1.0 - impactT);
          float star = specks((p - b) * 60.0 + floor(uTime * 20.0), 0.93)
            * smoothstep(0.2 * impactT, 0.0, dT0) * (1.0 - impactT);
          c += mix(uTint, vec3(1.0, 0.9, 0.6), 0.5) * (ball * 1.6 + star);
          body = max(body, ball + star);
        }
      } else if (isType(2.0)) {
        // ---- ICE V2: 6-fold crystal head + frost mist + impact spikes ----
        float angL = atan(p.y - head.y, p.x - head.x);
        float spike = pow(abs(cos(angL * 3.0 + travelT * 4.0)), 8.0);
        float crystalR = 0.028 + 0.05 * spike;
        float crystal = smoothstep(crystalR, crystalR * 0.35, dHead);
        float halo = smoothstep(0.1, 0.0, dHead) * 0.35;

        float mist = smoothstep(0.06, 0.0, dTrail) * step(along, headPos)
          * noise(p * 30.0 - uTime * vec2(3.0, 1.0)) * 0.7;
        float glint = specks(p * 90.0 + floor(uTime * 12.0), 0.97)
          * smoothstep(0.08, 0.0, dTrail);

        body = (crystal * 1.7 + halo + mist * 0.6 + glint) * headFade;
        c = mix(uTint, vec3(0.9, 0.98, 1.0), crystal) * body;

        float dT0 = length(p - b);
        if (impactT > 0.0 && dT0 < 0.32) {
          float angI = dT0 > 1e-5 ? atan(p.y - b.y, p.x - b.x) : 0.0;
          float spikes = pow(abs(cos(angI * 4.0 + 0.4)), 10.0);
          float reach = impactT * 0.16;
          float iceSpikes = spikes * smoothstep(reach, reach * 0.5, dT0)
            * smoothstep(0.0, 0.35, impactT) * (1.0 - impactT * 0.6);
          float frostRing = smoothstep(0.015, 0.0, abs(dT0 - impactT * 0.13)) * (1.0 - impactT);
          c += mix(uTint, vec3(1.0), 0.6) * (iceSpikes * 1.8 + frostRing * 1.4);
          body = max(body, iceSpikes + frostRing);
        }
      } else if (isType(4.0)) {
        // ---- ARCANE V2: swirl orb + satellites + ribbon; rune-ring impact ----
        float angL = atan(p.y - head.y, p.x - head.x);
        float swirl = 0.5 + 0.5 * sin(angL * 3.0 - uTime * 12.0 + dHead * 40.0);
        float orb = smoothstep(0.05, 0.0, dHead);
        float halo = smoothstep(0.11, 0.0, dHead) * 0.4;

        // 3 satellites orbiting the head
        float sats = 0.0;
        for (int k = 0; k < 3; k++) {
          float aa = uTime * 9.0 + float(k) * 2.094;
          vec2 sp = head + vec2(cos(aa), sin(aa)) * 0.045;
          sats += smoothstep(0.011, 0.0, length(p - sp));
        }

        // wavy ribbon trail: sample point offset by a sine across the path
        float wave = sin(along * 28.0 - uTime * 24.0) * 0.014;
        float ribbon = smoothstep(0.008, 0.0, abs(offAxis - wave))
          * step(along, headPos) * (1.0 - travelT * 0.3);

        body = (orb * (1.1 + 0.6 * swirl) + halo + sats * 1.4 + ribbon) * headFade;
        c = mix(uTint, vec3(1.0), orb * swirl * 0.7) * body;

        float dT0 = length(p - b);
        if (impactT > 0.0 && dT0 < 0.32) {
          float angI = dT0 > 1e-5 ? atan(p.y - b.y, p.x - b.x) : 0.0;
          float dash = step(0.5, fract(angI * 1.2732 + impactT * 2.0)); // 8 dashes
          float rune = smoothstep(0.014, 0.0, abs(dT0 - impactT * 0.14)) * dash * (1.0 - impactT);
          float spiral = smoothstep(0.1 * (1.0 - impactT), 0.0, dT0)
            * (0.5 + 0.5 * sin(angI * 4.0 - impactT * 12.0)) * (1.0 - impactT);
          c += mix(uTint, vec3(1.0), 0.5) * (rune * 2.0 + spiral * 1.2);
          body = max(body, rune + spiral);
        }
      } else if (isType(5.0)) {
        // ---- EARTH V2: tumbling chunks + dust + pebbles; crack-star impact ----
        float chunks = 0.0;
        for (int k = 0; k < 3; k++) {
          float fk = float(k);
          float lag = fk * 0.06;
          vec2 ch = mix(a, b, max(headPos - lag, 0.0))
            + perp * (hash(vec2(fk, 3.0)) - 0.5) * 0.05;
          float angC = atan(p.y - ch.y, p.x - ch.x);
          // irregular tumbling silhouette
          float rr = 0.03 * (1.0 - fk * 0.22)
            * (1.0 + 0.35 * cos(angC * 4.0 + uTime * (9.0 + fk * 3.0)));
          chunks += smoothstep(rr, rr * 0.4, length(p - ch)) * (1.0 - fk * 0.25);
        }

        float dust = smoothstep(0.07, 0.0, dTrail) * step(along, headPos)
          * noise(p * 22.0 + uTime * vec2(-2.0, 1.5)) * 0.55;
        // pebbles dropping behind the chunks under gravity (round specks)
        float pebbles = specks(p * 60.0 + vec2(0.0, uTime * uTime * 2.5), 0.972)
          * smoothstep(0.09, 0.0, dTrail) * step(along, headPos);

        body = (chunks * 1.5 + dust + pebbles) * headFade;
        c = uTint * body * 0.95;
        c += uTint * dust * 0.5 * headFade;

        float dT0 = length(p - b);
        if (impactT > 0.0 && dT0 < 0.34) {
          float angI = dT0 > 1e-5 ? atan(p.y - b.y, p.x - b.x) : 0.0;
          float cracks = pow(abs(cos(angI * 2.5 + 0.8)), 5.0);
          float reach = impactT * 0.18;
          float crackStar = cracks * smoothstep(reach, reach * 0.4, dT0) * (1.0 - impactT * 0.7);
          // slow dust plume ring lingers past the flash
          float plume = smoothstep(0.05, 0.0, abs(dT0 - impactT * 0.12))
            * (1.0 - impactT * 0.5) * 0.6;
          c += uTint * (crackStar * 1.5 + plume);
          body = max(body, crackStar + plume * 0.8);
        }
      } else {
        // ---- PHYSICAL V2: flash cut (iaijutsu strike) ----
        // Charge flicker at the dealer, then the whole path lights up as one
        // instant blinding line that dissolves dealer→target while the target
        // erupts. Runs on its own timeline (hit lands at ~28%, not 74%) — the
        // shared impact block below skips physical.
        float charge = smoothstep(0.06, 0.0, length(p - a))
          * smoothstep(0.3, 0.05, t) * (0.5 + 0.5 * sin(uTime * 50.0));

        float appear = smoothstep(0.22, 0.26, t);
        float dissolve = clamp((t - 0.38) / 0.45, 0.0, 1.0);
        float drawn = step(dissolve, along);
        float dLine = segDist(p, a, b);
        float flick = 0.75 + 0.25 * step(0.5, fract(uTime * 45.0));
        float core = smoothstep(0.006, 0.0, dLine) * appear * drawn;
        float lineGlow = smoothstep(0.035, 0.0, dLine) * appear * drawn * 0.5;

        body = (core * 2.0 + lineGlow) * flick + charge;
        c = mix(uTint, vec3(1.0), 0.8) * (core * 2.6 + lineGlow) * flick
          + uTint * charge * 1.4;

        // eruption at the target the instant the cut lands
        float cutT = clamp((t - 0.28) / 0.55, 0.0, 1.0);
        float dT0 = length(p - b);
        if (cutT > 0.0 && dT0 < 0.34) {
          float angI = dT0 > 1e-5 ? atan(p.y - b.y, p.x - b.x) : 0.0;
          float star = pow(abs(cos(angI * 4.0 + 0.6)), 9.0)
            * smoothstep(cutT * 0.24, cutT * 0.08, dT0) * (1.0 - cutT);
          float ringC = smoothstep(0.02, 0.0, abs(dT0 - cutT * 0.19)) * (1.0 - cutT);
          float flashC = smoothstep(0.14 * (1.0 - cutT), 0.0, dT0)
            * (1.0 - cutT) * (1.0 - cutT);
          float debris = specks((p - b) * 60.0 + floor(uTime * 20.0), 0.94)
            * smoothstep(0.22 * cutT, 0.0, dT0) * (1.0 - cutT);
          c += mix(uTint, vec3(1.0), 0.7) * (star * 2.6 + ringC * 1.9 + flashC * 1.5 + debris * 1.2);
          body = max(body, star + ringC + flashC + debris);
        }
      }

      col = c;
      alpha = clamp(body, 0.0, 1.0);
    }

    // ---- SHARED IMPACT BURST at target ----
    // Physical (flash cut) skips this: its hit lands at ~28% on its own
    // timeline and a second late burst here would read as a double hit.
    float dT = length(p - b);
    if (impactT > 0.0 && dT < 0.3 && !isType(0.0)) {
      float ring = smoothstep(0.02, 0.0, abs(dT - impactT * 0.16)) * (1.0 - impactT);
      float flash = smoothstep(0.12 * (1.0 - impactT), 0.0, dT) * (1.0 - impactT) * (1.0 - impactT);
      vec3 burst = mix(uTint, vec3(1.0), 0.4) * (ring * 1.8 + flash * 1.4);
      col += burst;
      alpha = max(alpha, ring + flash);
    }

    col *= endFade;
    alpha *= endFade;
    if (alpha <= 0.001) discard;
    gl_FragColor = vec4(col, clamp(alpha, 0.0, 1.0));
  }
`
